
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface User {
  id: string;
  email: string;
}

interface GameHistoryProps {
  user: User | null;
  offlineMode?: boolean;
}

interface GameRecord {
  id: string;
  game_type: string;
  difficulty: string;
  bet_amount: number;
  is_win: boolean;
  reward_amount: number;
  ai_opponent: any;
  created_at: string;
  duration: number;
  tx_hash?: string;
}

export default function GameHistory({ user, offlineMode = false }: GameHistoryProps) {
  const [gameHistory, setGameHistory] = useState<GameRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'wins' | 'losses'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'reward' | 'type'>('date');

  useEffect(() => {
    loadGameHistory();
  }, [user]);

  const loadGameHistory = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Mock data with Web3 elements
      const mockHistory: GameRecord[] = [
        {
          id: '1',
          game_type: 'prediction',
          difficulty: 'medium',
          bet_amount: 25,
          is_win: true,
          reward_amount: 62,
          ai_opponent: { name: 'Smart Contract Alpha', avatar: '⛓️' },
          created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          duration: 45,
          tx_hash: '0x7f9e...3a4b'
        },
        {
          id: '2',
          game_type: 'strategy',
          difficulty: 'hard',
          bet_amount: 50,
          is_win: false,
          reward_amount: 25,
          ai_opponent: { name: 'DeFi Oracle', avatar: '🔮' },
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          duration: 120,
          tx_hash: '0x3c8d...9f2e'
        },
        {
          id: '3',
          game_type: 'luck',
          difficulty: 'easy',
          bet_amount: 10,
          is_win: true,
          reward_amount: 25,
          ai_opponent: { name: 'NFT Generator', avatar: '🎨' },
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
          duration: 30,
          tx_hash: '0x1a5f...7c8d'
        },
        {
          id: '4',
          game_type: 'prediction',
          difficulty: 'hard',
          bet_amount: 100,
          is_win: false,
          reward_amount: 50,
          ai_opponent: { name: 'Yield Optimizer', avatar: '📈' },
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
          duration: 90,
          tx_hash: '0x9b2c...4e1a'
        },
        {
          id: '5',
          game_type: 'strategy',
          difficulty: 'medium',
          bet_amount: 25,
          is_win: true,
          reward_amount: 62,
          ai_opponent: { name: 'DAO Validator', avatar: '🗳️' },
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
          duration: 75,
          tx_hash: '0x6d4e...2b9f'
        }
      ];

      setGameHistory(mockHistory);
    } catch (error) {
      console.error('Load game history error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = gameHistory.filter(game => {
    if (filter === 'wins') return game.is_win;
    if (filter === 'losses') return !game.is_win;
    return true;
  });

  const sortedHistory = [...filteredHistory].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'reward':
        return b.reward_amount - a.reward_amount;
      case 'type':
        return a.game_type.localeCompare(b.game_type);
      default:
        return 0;
    }
  });

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const gameDate = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - gameDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getGameTypeIcon = (type: string) => {
    switch (type) {
      case 'prediction': return 'ri-eye-line';
      case 'strategy': return 'ri-chess-line';
      case 'luck': return 'ri-dice-line';
      default: return 'ri-gamepad-line';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'hard': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-400">Loading blockchain transactions...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Filters and Controls */}
      <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 mb-8">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                filter === 'all' ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              All Transactions
            </button>
            <button
              onClick={() => setFilter('wins')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                filter === 'wins' ? 'bg-green-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Wins Only
            </button>
            <button
              onClick={() => setFilter('losses')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                filter === 'losses' ? 'bg-red-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Losses Only
            </button>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none pr-8"
          >
            <option value="date">Sort by Block</option>
            <option value="reward">Sort by Reward</option>
            <option value="type">Sort by Type</option>
          </select>
        </div>
      </div>

      {/* Game History List */}
      <div className="space-y-4">
        {sortedHistory.length === 0 ? (
          <div className="text-center py-12 bg-gray-900/30 backdrop-blur-sm border border-gray-700/30 rounded-xl">
            <div className="text-6xl mb-4">⛓️</div>
            <h3 className="text-xl font-bold mb-2">No Transactions Found</h3>
            <p className="text-gray-400">
              {filter === 'all' ? 'Start playing to see your blockchain history here!' : `No ${filter} found in your history.`}
            </p>
          </div>
        ) : (
          sortedHistory.map((game) => (
            <div
              key={game.id}
              className={`bg-gradient-to-r backdrop-blur-sm border rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                game.is_win
                  ? 'from-green-900/20 to-emerald-900/20 border-green-500/30'
                  : 'from-blue-900/20 to-cyan-900/20 border-blue-500/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Game Type Icon */}
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    game.is_win ? 'bg-green-500/20' : 'bg-blue-500/20'
                  }`}>
                    <i className={`${getGameTypeIcon(game.game_type)} text-xl ${
                      game.is_win ? 'text-green-400' : 'text-blue-400'
                    }`}></i>
                  </div>

                  {/* Game Details */}
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="text-lg font-bold capitalize">
                        {game.game_type} Game
                      </h4>
                      <span className={`text-sm font-semibold ${getDifficultyColor(game.difficulty)}`}>
                        {game.difficulty.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        game.is_win ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {game.is_win ? 'WIN' : 'LOSS'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>vs {game.ai_opponent.name} {game.ai_opponent.avatar}</span>
                      <span>•</span>
                      <span>{formatTimeAgo(game.created_at)}</span>
                      <span>•</span>
                      <span className="font-mono text-cyan-400">{game.tx_hash}</span>
                    </div>
                  </div>
                </div>

                {/* Rewards */}
                <div className="text-right">
                  <div className="text-2xl font-bold text-yellow-400 mb-1">
                    +{game.reward_amount} PEP
                  </div>
                  <div className="text-sm text-gray-400">
                    Staked: {game.bet_amount} PEP
                  </div>
                  <div className={`text-xs font-semibold ${
                    game.reward_amount > game.bet_amount ? 'text-green-400' : 'text-gray-400'
                  }`}>
                    {game.reward_amount > game.bet_amount ? '+' : ''}{((game.reward_amount - game.bet_amount) / game.bet_amount * 100).toFixed(0)}% yield
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary Stats */}
      {sortedHistory.length > 0 && (
        <div className="mt-8 bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-sm border border-purple-500/30 rounded-xl p-6">
          <h3 className="text-lg font-bold mb-4">On-Chain Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{sortedHistory.length}</div>
              <div className="text-sm text-gray-400">Transactions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">
                {sortedHistory.filter(g => g.is_win).length}
              </div>
              <div className="text-sm text-gray-400">Wins</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {sortedHistory.reduce((sum, g) => sum + g.reward_amount, 0)}
              </div>
              <div className="text-sm text-gray-400">Total Yield</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">
                {Math.round(sortedHistory.reduce((sum, g) => sum + g.duration, 0) / 60)}m
              </div>
              <div className="text-sm text-gray-400">Block Time</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
